import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Check, PartyPopper, Camera, Share2, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

import gallery6 from "@/assets/gallery-6.jpg";

interface Package {
  id: string;
  name: string;
  price: number;
  features: string[];
  is_featured: boolean;
  description: string | null;
}

interface Addon {
  id: string;
  name: string;
  price: number;
  description: string | null;
}

const Photobooth = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroImage, setHeroImage] = useState({ url: gallery6, alt: "Wedding party having fun" });

  useEffect(() => {
    const fetchData = async () => {
      const [packagesRes, addonsRes] = await Promise.all([
        supabase
          .from("pricing_packages")
          .select("*")
          .eq("is_active", true)
          .eq("category", "photobooth")
          .order("sort_order"),
        supabase
          .from("pricing_addons")
          .select("*")
          .eq("is_active", true)
          .eq("category", "photobooth")
          .order("sort_order"),
      ]);

      if (packagesRes.data) {
        const sorted = [...packagesRes.data].sort((a, b) => b.price - a.price);
        const featuredIdx = sorted.findIndex(p => p.is_featured);
        if (featuredIdx !== -1 && featuredIdx !== 1 && sorted.length > 1) {
          const [featured] = sorted.splice(featuredIdx, 1);
          sorted.splice(1, 0, featured);
        }
        setPackages(sorted);
      }
      if (addonsRes.data) setAddons(addonsRes.data);

      try {
        const { data: locations } = await supabase
          .from("portfolio_media_locations")
          .select("media_id")
          .eq("location", "photobooth_page");

        if (locations && locations.length > 0) {
          const mediaIds = locations.map((l) => l.media_id);
          const { data: mediaData } = await supabase
            .from("portfolio_media")
            .select("*")
            .in("id", mediaIds)
            .eq("is_active", true)
            .eq("media_type", "image")
            .order("sort_order", { ascending: true })
            .limit(1);

          if (mediaData && mediaData.length > 0) {
            setHeroImage({
              url: mediaData[0].url,
              alt: mediaData[0].alt_text || mediaData[0].title,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching portfolio media:", error);
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(price);

  const valueProps = [
    {
      icon: Camera,
      title: "High-Quality Photos",
      description: "Professional DSLR camera with studio lighting for perfect shots every time.",
    },
    {
      icon: Sparkles,
      title: "Fun Props",
      description: "Extensive collection of signs, hats, glasses, and themed props included.",
    },
    {
      icon: Share2,
      title: "Instant Sharing",
      description: "Digital gallery with social media sharing and instant print options.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Wedding Photo Booth Rental | Pixie Clicks | NJ, FL & CO</title>
        <meta
          name="description"
          content="Fun photo booth experiences for your wedding reception. Props, instant prints & digital gallery included. Serving NJ, FL & CO."
        />
        <meta property="og:title" content="Wedding Photo Booth Rental | Pixie Clicks" />
        <meta property="og:description" content="Fun photo booth experiences for your wedding reception. Props, instant prints & digital gallery included." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pixieclicks.com/photobooth" />
        <link rel="canonical" href="https://pixieclicks.com/photobooth" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Wedding Photo Booth",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Pixie Clicks",
              "areaServed": ["New Jersey", "Florida", "Colorado"]
            },
            "description": "Fun photo booth experiences for wedding receptions with props, instant prints, and digital gallery."
          })}
        </script>
      </Helmet>
      <Layout>
        {/* Hero */}
        <section className="relative py-20 md:py-32 bg-muted/30">
          <div className="container-wide">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-primary">
                  <PartyPopper className="h-5 w-5" />
                  <span className="font-medium">Add Some Fun!</span>
                </div>
                <h1 className="mt-6 font-serif text-4xl font-semibold leading-tight text-foreground md:text-5xl">
                  Photo Booth <span className="text-primary">Experience</span>
                </h1>
                <p className="mt-6 text-lg text-muted-foreground">
                  Give your guests an unforgettable experience! Our modern photo booth combines high-quality photography with instant fun, creating memories and keepsakes your guests will love.
                </p>
                <Button variant="hero" size="lg" className="mt-8" asChild>
                  <Link to="/contact">Add to Your Package</Link>
                </Button>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <img
                  src={heroImage.url}
                  alt={heroImage.alt}
                  className="rounded-2xl shadow-elevated"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="section-padding">
          <div className="container-wide">
            <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
              What's Included
            </h2>
            <div className="mt-12 grid gap-8 md:grid-cols-3">
              {valueProps.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <item.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="section-padding bg-muted/30">
          <div className="container-wide">
            <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Photo Booth Packages
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-muted-foreground">
              Everything you need for reception fun
            </p>

            {loading ? (
              <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className="h-80 animate-pulse rounded-xl bg-muted"
                  />
                ))}
              </div>
            ) : packages.length > 0 ? (
              <div className={`mt-12 grid gap-6 ${packages.length === 1 ? 'max-w-md mx-auto' : packages.length === 2 ? 'md:grid-cols-2 max-w-2xl mx-auto' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {packages.map((pkg, index) => (
                  <motion.div
                    key={pkg.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`relative flex flex-col rounded-xl border p-6 ${
                      pkg.is_featured
                        ? "border-primary bg-primary/5 shadow-rose"
                        : "border-border bg-card"
                    }`}
                  >
                    {pkg.is_featured && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                        Most Popular
                      </span>
                    )}
                    <h3 className="font-serif text-xl font-semibold text-foreground">
                      {pkg.name}
                    </h3>
                    <p className="mt-2 text-3xl font-bold text-primary">
                      {formatPrice(pkg.price)}
                    </p>
                    {pkg.description && (
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                        {pkg.description}
                      </p>
                    )}
                    <ul className="mt-6 flex-1 space-y-2">
                      {pkg.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-2 text-sm"
                        >
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <span className="text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant={pkg.is_featured ? "hero" : "outline"}
                      className="mt-6 w-full"
                      asChild
                    >
                      <Link to={`/contact?package=${encodeURIComponent(pkg.name)}`}>Check Availability</Link>
                    </Button>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="mt-12 rounded-2xl bg-card border border-border p-8 md:p-12 max-w-2xl mx-auto">
                <div className="text-center">
                  <p className="text-muted-foreground">Starting at</p>
                  <p className="text-4xl font-bold text-foreground mt-2">Contact for pricing</p>
                </div>
                <div className="mt-8 grid gap-4 md:grid-cols-2">
                  {[
                    "Up to 4 hours of coverage",
                    "Professional attendant",
                    "Unlimited sessions",
                    "Full prop collection",
                    "Custom photo template",
                    "Instant prints for guests",
                    "Digital gallery access",
                    "Social media sharing",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-foreground">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 text-center">
                  <Button variant="hero" size="lg" asChild>
                    <Link to="/contact?package=Photo%20Booth">Check Availability</Link>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>

      </Layout>
    </>
  );
};

export default Photobooth;
